// SPDX-License-Identifier: GPL-3.0-only
// Copyright (C) 2026 Kyle Johnson

import { v4 as uuidv4 } from 'uuid';
import type { ReviewedTransaction } from './types.js';
import type { TransactionPostedPayload, LedgerState } from '../../types.js';
import type { Account } from '../../types.js';

/**
 * Convert a reviewed (confirmed) transaction into a double-entry
 * TransactionPostedPayload. The "other side" of each transaction is posted to
 * an imbalance account (similar to GnuCash's Imbalance-USD account) when no
 * transfer account is known.
 *
 * If the transaction has a transferAccountId, use that as the other split.
 * Otherwise, use the provided imbalanceAccountId.
 */
export function buildTransactionPayload(
  tx: ReviewedTransaction,
  importSessionId: string,
  ledgerState: LedgerState,
  imbalanceAccountId: string,
): TransactionPostedPayload {
  const mainSplitId = uuidv4();
  const otherSplitId = uuidv4();
  const transactionId = uuidv4();

  const otherAccountId = tx.transferAccountId ?? imbalanceAccountId;

  // Verify the main account exists, fall back to imbalance
  const mainAccount: Account | undefined = ledgerState.accounts.get(tx.accountId);
  const currency = mainAccount?.currency ?? tx.currency;

  return {
    transactionId,
    date: tx.date,
    dateEntered: new Date().toISOString(),
    description: tx.description,
    notes: tx.notes ?? tx.memo ?? '',
    currency,
    ...(tx.num ? { num: tx.num } : {}),
    ...(tx.fitid ? { fitid: tx.fitid } : {}),
    importSessionId,
    splits: [
      {
        splitId: mainSplitId,
        accountId: tx.accountId,
        amount: tx.amount,
        quantity: tx.amount,
        reconcileState: 'n',
        reconcileDate: null,
        memo: tx.memo ?? '',
        action: '',
      },
      {
        splitId: otherSplitId,
        accountId: otherAccountId,
        amount: -tx.amount,
        quantity: -tx.amount,
        reconcileState: 'n',
        reconcileDate: null,
        memo: '',
        action: '',
      },
    ],
  };
}

/**
 * Find or create an imbalance account ID. Returns the ID of an existing
 * imbalance account for the given currency, or null if none exists.
 */
export function findImbalanceAccount(ledgerState: LedgerState, currency: string): string | null {
  for (const account of ledgerState.accounts.values()) {
    if (
      account.name.toLowerCase().startsWith('imbalance') &&
      account.currency === currency
    ) {
      return account.id;
    }
  }
  return null;
}
